import { Link, Popover, Stack, Text } from "@hopper-ui/components";
import { type RefObject, useRef, useState } from "react";
import { useShellTranslation } from "../../../i18n/i18next.ts";
import styles from "./SwitcherTipPopover.module.css";
import { consumeSwitcherTip } from "./switcherTipStorage.ts";

export interface SwitcherTipPopoverProps {
    triggerRef: RefObject<HTMLButtonElement | null>;
    workspaceId: string;
    workspaceName: string;
}

export function SwitcherTipPopover({ triggerRef, workspaceId, workspaceName }: SwitcherTipPopoverProps) {
    const { t } = useShellTranslation();
    const popoverRef = useRef<HTMLElement>(null);
    // Read-and-clear on mount so a marker left behind by an abandoned switch can never re-open the tip.
    const [isOpen, setIsOpen] = useState(() => consumeSwitcherTip() === workspaceId);

    function shake() {
        const node = popoverRef.current;

        if (!node || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
            return;
        }

        node.animate(
            [
                { translate: "0 0" },
                { translate: "-0.5rem 0", offset: 0.1 },
                { translate: "0.4375rem 0", offset: 0.25 },
                { translate: "-0.375rem 0", offset: 0.4 },
                { translate: "0.3125rem 0", offset: 0.55 },
                { translate: "-0.1875rem 0", offset: 0.7 },
                { translate: "0.125rem 0", offset: 0.85 },
                { translate: "0 0", offset: 1 }
            ],
            { duration: 500, easing: "ease-out" }
        );
    }

    return (
        <Popover
            ref={popoverRef}
            className={styles.popover}
            triggerRef={triggerRef}
            isOpen={isOpen}
            placement="bottom end"
            isNonDialog
            shouldCloseOnInteractOutside={() => {
                shake();

                return false;
            }}
            UNSAFE_maxWidth="18rem"
        >
            <Stack gap="stack-sm" padding="inset-md">
                <Text fontWeight="body-sm-semibold" fontSize="body-sm">
                    {t("SwitcherTipPopover.title", { workspaceName })}
                </Text>
                <Text fontSize="body-sm" color="neutral-weak" data-public>
                    {t("SwitcherTipPopover.description")}
                </Text>
                <Link onPress={() => setIsOpen(false)} data-public>
                    {t("SwitcherTipPopover.dismiss")}
                </Link>
            </Stack>
        </Popover>
    );
}
